<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Ticketing from "@/services/Ticketing";
import Swal from "sweetalert2";

export default {
  name: "TicketingSectionSetup",
  components: { Layout, PageHeader },
  page: {
    title: "Section Overrides",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      title: "Section Overrides",
      items: [
        {
          text: "Ticketing",
        },
        {
          text: "Section Overrides",
          active: true,
          href: "/ticketing/section_setup",
        },
      ],
      isBusy: false,
      error: null,
      tableData: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      sortBy: "section",
      sortDesc: true,
      newSection: "",
      newMaxCapacity: "",
      addLoading: false,
      fields: [
        {
          key: "section",
          label: "Section",
          sortable: true,
        },
        {
          key: "max_capacity",
          label: "Max capacity",
          sortable: true,
        },
        "action",
      ],
    };
  },
  async created() {
    await this.getSections();
  },
  methods: {
    async getSections() {
      try {
        this.toggleBusy();
        const response = await Ticketing.getTicketingSectionOverwrite();
        // Assume API returns { data: [...] } where each item has "section" field
        this.tableData = response.data.data || [];
        this.totalRows = this.tableData.length;
      } catch (error) {
        this.error = error.response?.data?.error || error.message || "Failed to load sections";
        this.tableData = [];
        this.totalRows = 0;
      } finally {
        this.toggleBusy();
      }
    },
    async addSection() {
      const section = (this.newSection || "").trim();
      const maxCapacity = this.newMaxCapacity !== null && this.newMaxCapacity !== undefined
        ? String(this.newMaxCapacity).trim()
        : "";

      if (!section) {
        Swal.fire("Validation", "Section value is required", "warning");
        return;
      }
      if (!maxCapacity || isNaN(Number(maxCapacity))) {
        Swal.fire("Validation", "Max capacity must be a number", "warning");
        return;
      }
      try {
        this.addLoading = true;
       
        await Ticketing.saveTicketingSectionOverwrite({
          section,
          max_capacity: Number(maxCapacity),
        });
        this.newSection = "";
        this.newMaxCapacity = "";
        Swal.fire("Success", "Section has been added", "success").then(async () => {
          await this.getSections();
        });
      } catch (error) {
        const msg = error.response?.data?.error || error.message || "Failed to add section";
        Swal.fire("Error", msg, "error");
      } finally {
        this.addLoading = false;
      }
    },
    async deleteSection(item) {
      const section = item.section;
      Swal.fire({
        title: "Remove section?",
        text: `Section "${section}" will be removed!`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Remove!",
      }).then(async (result) => {
        if (!result.value) return;
        try {
          await Ticketing.deleteTicketingSectionOverwrite(section);
          Swal.fire("Removed", "Section was successfully removed!", "success").then(
            async () => {
              await this.getSections();
            }
          );
        } catch (error) {
          const msg = error.response?.data?.error || error.message || "Failed to remove section";
          Swal.fire("Error", msg, "error");
        } 
      });
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    toggleBusy() {
      this.isBusy = !this.isBusy;
    },
  },
  middleware: "authentication",
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <b-alert variant="info" show class="mb-3">
          <strong>Note:</strong>
          For the sections defined here, the tickets in the app are displayed as
          standing tickets only (without row and seat information).
        </b-alert>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-md-4">
                <b-form-group label="Section" label-for="new-section-input" class="mb-3">
                  <b-form-input
                    id="new-section-input"
                    v-model="newSection"
                    placeholder="Enter section name..."
                  ></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-3">
                <b-form-group label="Max capacity" label-for="new-max-capacity-input" class="mb-3">
                  <b-form-input
                    id="new-max-capacity-input"
                    v-model="newMaxCapacity"
                    type="number"
                    min="0"
                    placeholder="Enter max capacity..."
                  ></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-2 d-flex align-items-end align-items-center mt-3">
                <button
                  type="button"
                  class="btn btn-success"
                  @click="addSection"
                  :disabled="addLoading"
                >
                  <span
                    v-if="addLoading"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <i v-else class="mdi mdi-plus me-1"></i>
                  Add
                </button>
              </div>
            </div>

            <div class="row mt-2">
              <div class="col-sm-12 col-md-6">
                <div id="sections-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select
                      v-model="perPage"
                      size="sm"
                      :options="pageOptions"
                    ></b-form-select
                    >&nbsp;entries
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div id="sections-table_filter" class="dataTables_filter text-md-end">
                  <label class="d-inline-flex align-items-center">
                    Search:
                    <b-form-input
                      v-model="filter"
                      type="search"
                      placeholder="Search..."
                      class="form-control form-control-sm ms-2"
                    ></b-form-input>
                  </label>
                </div>
              </div>
            </div>

            <div class="table-responsive mb-0">
              <b-table
                :busy="isBusy"
                :items="tableData"
                :fields="fields"
                responsive="sm"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                show-empty
                empty-text="No Data Found"
                @filtered="onFiltered"
              >
                <template v-slot:cell(action)="data">
                  <ul class="list-inline mb-0">
                    <li class="list-inline-item">
                      <a
                        href="javascript:void(0);"
                        class="px-2 text-danger"
                        title="Remove section"
                        @click="deleteSection(data.item)"
                      >
                        <i class="uil uil-trash-alt font-size-18"></i>
                      </a>
                    </li>
                  </ul>
                </template>

                <template #table-busy>
                  <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Loading...</strong>
                  </div>
                </template>
              </b-table>
            </div>

            <div class="row mt-3">
              <div class="col-sm-12 col-md-5">
                <div class="dataTables_info">
                  Showing {{ (currentPage - 1) * perPage + 1 }}
                  to
                  {{ Math.min((currentPage - 1) * perPage + perPage, totalRows) }}
                  of {{ totalRows }} entries
                </div>
              </div>
              <!-- <div class="col-sm-12 col-md-7">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  align="right"
                  size="sm"
                ></b-pagination>
              </div> -->
              <div class="col-12">
                                <div class="dataTables_paginate paging_simple_numbers float-end">
                                    <ul class="pagination pagination-rounded mb-0">
                                        <!-- pagination -->
                                        <b-pagination
                                                v-model="currentPage"
                                                :total-rows="totalRows"
                                                :per-page="perPage"
                                        ></b-pagination>
                                    </ul>
                                </div>
                            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

